import { CatalogManager, ImageInfoRecord, PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog"
import {
	createImageInfo,
	ImageFeature,
	SceneInfo,
	SceneWhiteBalance,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { getFileFormat } from "@/utils/file"
import { AI } from "@/lib/ai/ai"
import { debounced } from "@/utils/debounce"
import {
	currentPhotoAtom,
	getCullingInfo,
	getPhotoMetadata,
	updatePhotoMetadata,
} from "@/store/photo/photos-state"
import { PhotoId } from "@/store/undoable-state"
import { atom } from "jotai"
import { didFlashFire } from "@/utils/exif"
import { getFileForPhoto } from "@/store/files-state"
import { store } from "../store"
import { MetadataLoader } from "@/lib/metadata/metadata-loader"
import { isAIDebugEnabled } from "@/utils/measurement/feature-flag"
import { isAIEditingModeAtom } from "../project/projects-state"
import * as Sentry from "@sentry/react"
import { clearAllColorMatchRuntimeInfo } from "../debug/color-match-runtime-state"

export const imageInfosAtom = atom<Record<PhotoId, ImageInfoRecord>>({})

class ImageInfoStore {
	private records = new Map<PhotoId, ImageInfoRecord>()
	private updatedRecords = new Set<PhotoId>()

	removeAll() {
		this.records.clear()
		this.updatedRecords.clear()
		clearAllColorMatchRuntimeInfo()

		if (isAIDebugEnabled() && store.get(isAIEditingModeAtom)) {
			store.set(imageInfosAtom, {})
		}
	}

	setAll(records: Record<PhotoId, ImageInfoRecord>) {
		this.records = new Map(Object.entries(records))
		clearAllColorMatchRuntimeInfo()

		if (isAIDebugEnabled() && store.get(isAIEditingModeAtom)) {
			const newMap = Object.fromEntries(this.records) as Record<PhotoId, ImageInfoRecord>
			store.set(imageInfosAtom, newMap)
		}
	}

	getAll() {
		return this.records
	}

	set(record: ImageInfoRecord) {
		this.records.set(record.imageId, record)
		this.updatedRecords.add(record.imageId)
		this.storeUpdates()

		if (isAIDebugEnabled() && store.get(isAIEditingModeAtom)) {
			const items = store.get(imageInfosAtom)
			store.set(imageInfosAtom, { ...items, [record.imageId]: record })
		}
	}

	get(id: PhotoId) {
		return this.records.get(id)
	}

	@debounced()
	private storeUpdates() {
		for (const id of this.updatedRecords.values()) {
			const record = this.records.get(id)
			if (record) {
				CatalogManager.storeImageInfoRecord(record)
			}
		}
		this.updatedRecords.clear()
	}
}

const imageInfoStore = new ImageInfoStore()

export function setImageInfoRecords(newRecords: Record<PhotoId, ImageInfoRecord>) {
	imageInfoStore.setAll(newRecords)
}

export function getImageInfoRecords() {
	return imageInfoStore.getAll()
}

/**
 * Sync existing imageInfoStore records to imageInfosAtom when debug mode is enabled
 * This is needed because the atom is only populated when debug mode is on,
 * but records may have been loaded earlier when debug mode was off
 */
export function syncImageInfosToAtom() {
	if (isAIDebugEnabled() && store.get(isAIEditingModeAtom)) {
		const allRecords = imageInfoStore.getAll()
		const recordsMap = Object.fromEntries(allRecords) as Record<PhotoId, ImageInfoRecord>
		store.set(imageInfosAtom, recordsMap)
	}
}

export function getImageInfo(id: PhotoId) {
	const imageInfo = imageInfoStore.get(id)

	// Backward compatibility: imageInfo.features.meta.format might be undefined from old project
	if (imageInfo && imageInfo.features.meta.format === undefined) {
		const file = getFileForPhoto(id)
		if (file) imageInfo.features.meta.format = getFileFormat(file)
	}

	return imageInfo
}

export function updateImageInfo(imageInfo: ImageInfoRecord) {
	imageInfoStore.set(imageInfo)
}

export function resetBiasesInImageInfo(id: PhotoId) {
	const imageInfo = getImageInfo(id)
	if (imageInfo) {
		const updatedInfo: ImageInfoRecord = { ...imageInfo, biases: null }
		updateImageInfo(updatedInfo)
	}
}

export function resetAllImageInfo() {
	imageInfoStore.removeAll()
}

export function getCameraModel(metadata: PhotoMetadataRecord) {
	return metadata.cameraModel ?? metadata.extension
}

function createImageFeatures(input: {
	sceneInfo: SceneInfo
	metadata?: PhotoMetadataRecord
	whiteBalance: SceneWhiteBalance
	fileName: string
}): ImageFeature {
	return {
		...input.sceneInfo,
		meta: {
			iso: input.metadata?.iso ?? null,
			timestamp: input.metadata?.dateCreated ?? input.metadata?.dateModified ?? null,
			flash: didFlashFire(input.metadata?.flash ?? null),
			camera: input.metadata ? getCameraModel(input.metadata) : null,
			init_temp: input.whiteBalance.asShotTemperature,
			init_tint: input.whiteBalance.asShotTint,
			format: getFileFormat(input.fileName),
		},
	}
}

export async function getOrExtractImageInfoWithLock(photo: PhotoRecord) {
	const imageInfo = getImageInfo(photo.id)
	if (imageInfo) {
		return imageInfo
	}

	try {
		await AI.lockEditor()
		return await extractImageInfo(photo)
	} finally {
		AI.unlockEditor()
	}
}

export async function getOrExtractImageInfo(photo: PhotoRecord) {
	const imageInfo = getImageInfo(photo.id)
	if (imageInfo) {
		return imageInfo
	}

	return await extractImageInfo(photo)
}

async function extractImageInfo(photo: PhotoRecord) {
	const file = getFileForPhoto(photo)
	if (!file) {
		throw new Error("Cannot extract image info. File is missing")
	}

	let metadata = getPhotoMetadata(photo.id)
	if (!metadata || !metadata.isComplete) {
		try {
			metadata = (await MetadataLoader.getMetadata(file)).record
			updatePhotoMetadata(photo.id, metadata)
		} catch (error) {
			// Even if metadata extraction failed, raw rendering might still work, so continue.
			// Normally, unsupported files would be filtered out before this point.
			console.error(
				`Error extracting metadata when creating imageInfo for ${photo.fileName}: ${error}`,
			)
			Sentry.captureException(error)
		}
	}

	const cullingInfo = getCullingInfo(photo.id)

	await AI.loadImage(file, { metadata })
	const { sceneInfo, whiteBalance, sceneBinaryData } = await AI.extractSceneInfo(cullingInfo)
	const imageFeatures = createImageFeatures({
		sceneInfo,
		whiteBalance,
		metadata,
		fileName: photo.fileName,
	})
	const newInfo = createImageInfo(photo.id, imageFeatures, sceneBinaryData)

	// Enable this if we ever support auto button for AI mode
	// const aiAdjustments = await AI.getDefaultAutoParamsFast(photo, newInfo)
	// if (aiAdjustments)
	// 	newInfo.aiAdjustmentsMap = { [nullPresetID]: aiAdjustments }

	updateImageInfo(newInfo)

	return newInfo
}

export const currentImageInfoAtom = atom(
	(get) => {
		const currentPhoto = get(currentPhotoAtom)

		if (currentPhoto) {
			return getImageInfo(currentPhoto.id)
		} else {
			return null
		}
	},
	(get, _, updatedInfo: ImageInfoRecord) => {
		const photo = get(currentPhotoAtom)

		if (photo) {
			updateImageInfo(updatedInfo)
		}
	},
)
